import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { useArtefactTemplates } from '../../../hooks'
import { VelociraptorTemplateStep } from '../../../components/collections/VelociraptorTemplateStep'
import type { CaseEndpoint, CreateCollectionRequestInput } from '../../../types/case-container'
import type { EndpointSource } from '../../../types/collection-set'
import { EndpointSourceSelector } from './EndpointSourceSelector'

const TEXTAREA_ROWS = 3

interface CreateCollectionModalProps {
  caseId: string
  onClose: () => void
  onCreate: (data: CreateCollectionRequestInput) => void
  creating: boolean
  error: string | null
  endpoints: CaseEndpoint[]
  collectionSetIds: Set<string>
  collectionSetCount: number
}

interface TopFieldsProps {
  formData: CreateCollectionRequestInput
  setFormData: (data: CreateCollectionRequestInput) => void
}

function TopFields({ formData, setFormData }: TopFieldsProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('collections.requestName')}
        </label>
        <input
          type="text"
          value={formData.requestName}
          onChange={(event) => {
            setFormData({ ...formData, requestName: event.target.value })
          }}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          placeholder={t('collections.requestNamePlaceholder')}
          required
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('collections.collectorType')}
        </label>
        <select
          value={formData.collectorType}
          onChange={(event) => {
            setFormData({ ...formData, collectorType: event.target.value })
          }}
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        >
          <option value="velociraptor">Velociraptor</option>
          <option value="kape">KAPE</option>
          <option value="x-ways">X-Ways</option>
          <option value="custom">Custom</option>
          <option value="other">Other</option>
        </select>
      </div>
    </>
  )
}

interface TemplatePickerProps {
  caseId: string
  selectedTemplateId: string | null
  setSelectedTemplateId: (value: string | null) => void
}

function TemplatePicker(props: TemplatePickerProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { caseId, selectedTemplateId, setSelectedTemplateId } = props
  const { templates, loading, refetch } = useArtefactTemplates(caseId)
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.templateStep.label', 'Collector Template')}
      </label>
      <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <VelociraptorTemplateStep
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          onSelect={setSelectedTemplateId}
          loading={loading}
          caseId={caseId}
          userRole="case_user"
          onTemplatesChanged={() => {
            void refetch()
          }}
        />
      </div>
    </div>
  )
}

interface NotesFieldProps {
  formData: CreateCollectionRequestInput
  setFormData: (data: CreateCollectionRequestInput) => void
}

function NotesField({ formData, setFormData }: NotesFieldProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('collections.notes')}
      </label>
      <textarea
        value={formData.notes ?? ''}
        onChange={(event) => {
          setFormData({ ...formData, notes: event.target.value })
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        rows={TEXTAREA_ROWS}
        placeholder={t('collections.notesPlaceholder')}
      />
    </div>
  )
}

interface SummaryRowProps {
  count: number
}

function SummaryRow({ count }: SummaryRowProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
      <p className="text-slate-600 dark:text-slate-400">
        {t('collections.targetSummary', { count })}
      </p>
    </div>
  )
}

interface FooterProps {
  onClose: () => void
  creating: boolean
  disabled: boolean
}

function Footer({ onClose, creating, disabled }: FooterProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {creating && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('collections.createRequest')}
      </button>
    </div>
  )
}

interface SourceCounts {
  itSystemEndpoints: CaseEndpoint[]
  collectionSetIds: Set<string>
  collectionSetCount: number
  chosenEndpointIds: Set<string>
}

function targetIdsForSource(source: EndpointSource, counts: SourceCounts): string[] {
  switch (source) {
    case 'all':
      return counts.itSystemEndpoints.map((endpoint) => endpoint.id)
    case 'collection_set':
      return Array.from(counts.collectionSetIds)
    case 'choose':
      return Array.from(counts.chosenEndpointIds)
    default:
      return []
  }
}

function previewCountForSource(source: EndpointSource, counts: SourceCounts): number {
  switch (source) {
    case 'all':
      return counts.itSystemEndpoints.length
    case 'collection_set':
      return counts.collectionSetCount
    case 'choose':
      return counts.chosenEndpointIds.size
    default:
      return 0
  }
}

interface CreateFormState {
  formData: CreateCollectionRequestInput
  setFormData: (data: CreateCollectionRequestInput) => void
  endpointSource: EndpointSource
  setEndpointSource: (source: EndpointSource) => void
  selectedTemplateId: string | null
  setSelectedTemplateId: (id: string | null) => void
  chosenEndpointIds: Set<string>
  toggleChosenEndpoint: (id: string) => void
}

function useCreateFormState(initialSource: EndpointSource): CreateFormState {
  const [formData, setFormData] = useState<CreateCollectionRequestInput>({
    requestName: '',
    collectorType: 'velociraptor',
    targetEndpointIds: [],
  })
  const [endpointSource, setEndpointSource] = useState<EndpointSource>(initialSource)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [chosenEndpointIds, setChosenEndpointIds] = useState<Set<string>>(new Set())
  const toggleChosenEndpoint = (id: string): void => {
    setChosenEndpointIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  return {
    formData,
    setFormData,
    endpointSource,
    setEndpointSource,
    selectedTemplateId,
    setSelectedTemplateId,
    chosenEndpointIds,
    toggleChosenEndpoint,
  }
}

interface CreateFormBodyProps {
  caseId: string
  state: CreateFormState
  itSystemEndpoints: CaseEndpoint[]
  collectionSetCount: number
}

function CreateFormBody(props: CreateFormBodyProps): React.ReactElement {
  const { caseId, state, itSystemEndpoints, collectionSetCount } = props
  return (
    <>
      <TopFields formData={state.formData} setFormData={state.setFormData} />
      {state.formData.collectorType === 'velociraptor' && (
        <TemplatePicker
          caseId={caseId}
          selectedTemplateId={state.selectedTemplateId}
          setSelectedTemplateId={state.setSelectedTemplateId}
        />
      )}
      <EndpointSourceSelector
        endpointSource={state.endpointSource}
        setEndpointSource={state.setEndpointSource}
        itSystemEndpoints={itSystemEndpoints}
        collectionSetCount={collectionSetCount}
        chosenEndpointIds={state.chosenEndpointIds}
        toggleChosenEndpoint={state.toggleChosenEndpoint}
      />
      <NotesField formData={state.formData} setFormData={state.setFormData} />
    </>
  )
}

export function CreateCollectionModal(props: CreateCollectionModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { caseId, onClose, onCreate, creating, error, endpoints } = props
  const { collectionSetIds, collectionSetCount } = props
  const state = useCreateFormState(collectionSetCount > 0 ? 'collection_set' : 'all')

  const itSystemEndpoints = endpoints.filter((endpoint) => endpoint.nodeCategory === 'it_system')
  const counts: SourceCounts = {
    itSystemEndpoints,
    collectionSetIds,
    collectionSetCount,
    chosenEndpointIds: state.chosenEndpointIds,
  }
  const previewCount = previewCountForSource(state.endpointSource, counts)

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (!state.formData.requestName) return
    const targetEndpointIds = targetIdsForSource(state.endpointSource, counts)
    onCreate({
      ...state.formData,
      targetEndpointIds,
      ...(state.selectedTemplateId ? { artefactTemplateId: state.selectedTemplateId } : {}),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('collections.newCollectionRequest')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/50">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
          <CreateFormBody
            caseId={caseId}
            state={state}
            itSystemEndpoints={itSystemEndpoints}
            collectionSetCount={collectionSetCount}
          />
          <SummaryRow count={previewCount} />
          <Footer
            onClose={onClose}
            creating={creating}
            disabled={creating || !state.formData.requestName || previewCount === 0}
          />
        </form>
      </div>
    </div>
  )
}
